/**
 * API client for phases 3 to 5.
 *
 * Kept separate from `api.ts` so the operational surface (entry, looms, stock)
 * stays readable. Same request helper, same refusal handling — a 409 still
 * carries a structured `Blocked` payload the grid renders against a row.
 */

import { ApiError, getToken, request } from "./api";
import type {
  BatchRow,
  DemoLoadResult,
  DemoPurgeResult,
  DemoStatus,
  BillResult,
  CloseStatus,
  CostRunResult,
  CountResult,
  DispatchResult,
  DispatchRow,
  DriverRow,
  DueCount,
  GenerateResult,
  IntakePreview,
  MissingMappings,
  Sku,
  TemplateSpec,
  TransferResult,
  TransitRow,
  WeighbridgeReport,
  WipLadderResponse,
} from "./types";

// Same origin in production: the built app is served by the API itself, so
// an empty base makes every call relative. Only `npm run dev` needs an
// absolute host, and Vite's own proxy handles that.
const BASE =
  import.meta.env.VITE_API_BASE ??
  (import.meta.env.DEV ? "http://localhost:8000" : "");

export interface UnitRow {
  id: number;
  code: string;
  name: string;
  state: string;
  gstin: string | null;
}

export const ops = {
  // -- Masters ------------------------------------------------------------
  skus: () => request<Sku[]>("/api/masters/skus"),
  units: () => request<UnitRow[]>("/api/masters/units"),

  // -- Phase 3: dispatch --------------------------------------------------
  createDispatch: (payload: {
    party_code: string;
    business_date: string;
    vehicle_no?: string | null;
    lr_no?: string | null;
    destination?: string | null;
    lines: { sku_code: string; qty_pcs: number; rate?: number | null }[];
  }) =>
    request<DispatchResult>("/api/dispatch", {
      method: "POST",
      body: payload,
      queueable: true,
    }),

  weighDispatch: (
    id: number,
    body: { declared_wt_kg: number; bridge_wt_kg?: number | null }
  ) =>
    request<DispatchResult>(`/api/dispatch/${id}/weigh`, {
      method: "POST",
      body,
    }),

  closeDispatch: (id: number, overrideReason?: string) =>
    request<DispatchResult>(`/api/dispatch/${id}/close`, {
      method: "POST",
      body: { override_reason: overrideReason ?? null },
    }),

  dispatches: (from: string, to: string) =>
    request<DispatchRow[]>(`/api/dispatch?period_from=${from}&period_to=${to}`),

  weighbridge: (from: string, to: string) =>
    request<WeighbridgeReport>(
      `/api/dispatch/weighbridge-exceptions?period_from=${from}&period_to=${to}`
    ),

  // -- Phase 3: the WIP ladder --------------------------------------------
  wipLadder: (asOn: string, includeEmpty = false) =>
    request<WipLadderResponse>(
      `/api/wip?as_on=${asOn}&include_empty=${includeEmpty}`
    ),

  wipSnapshot: (asOn: string) =>
    request<{ as_on: string; skus_written: number }>(
      `/api/wip/snapshot?as_on=${asOn}`, { method: "POST" }
    ),

  wipReconcile: (asOn: string) =>
    request<{
      as_on: string;
      skus_checked: number;
      agrees: boolean;
      divergences: { sku_code: string; field: string; snapshot: string; ledger: string; difference: string }[];
      note: string;
    }>(`/api/wip/reconcile?as_on=${asOn}`),

  // -- Phase 3: inter-unit transfers --------------------------------------
  createTransfer: (payload: {
    from_unit_code: string;
    to_unit_code: string;
    dispatched_on: string;
    vehicle_no?: string | null;
    lines: { item_code: string; qty: number; transfer_rate?: number | null }[];
  }) =>
    request<TransferResult>("/api/transfer", { method: "POST", body: payload }),

  receiveTransfer: (
    id: number,
    body: { received_on: string; quantities?: Record<string, number> | null }
  ) =>
    request<{
      status: string;
      shortages: Record<string, string>;
      transit_days: number;
    }>(`/api/transfer/${id}/receive`, { method: "POST", body }),

  inTransit: (asOn?: string) =>
    request<TransitRow[]>(
      `/api/transfer/in-transit${asOn ? `?as_on=${asOn}` : ""}`
    ),

  ewayBill: (id: number) =>
    request<Record<string, unknown>>(`/api/transfer/${id}/eway-bill`),

  // -- Phase 4: Tally -----------------------------------------------------
  batches: () => request<BatchRow[]>("/api/tally/batches"),

  generateBatch: (from: string, to: string) =>
    request<GenerateResult>("/api/tally/batches", {
      method: "POST",
      body: { period_from: from, period_to: to },
    }),

  approveBatch: (id: number) =>
    request<{ batch_id: number; status: string }>(
      `/api/tally/batches/${id}/approve`,
      { method: "POST" }
    ),

  confirmBatch: (id: number) =>
    request<{ batch_id: number; status: string }>(
      `/api/tally/batches/${id}/confirm`,
      { method: "POST", body: {} }
    ),

  missingMappings: () => request<MissingMappings>("/api/tally/mappings/missing"),

  addMapping: (body: {
    entity: string;
    local_code: string;
    tally_name: string;
  }) => request("/api/tally/mappings", { method: "POST", body }),

  raiseBill: (body: {
    contractor_code: string;
    period_from: string;
    period_to: string;
  }) => request<BillResult>("/api/billing/contractor", { method: "POST", body }),

  // -- Phase 5: costing ---------------------------------------------------
  drivers: () => request<DriverRow[]>("/api/costing/drivers"),

  seedDrivers: (validFrom: string) =>
    request<{ created: number; note: string }>(
      `/api/costing/drivers/seed?valid_from=${validFrom}`,
      { method: "POST" }
    ),

  setDriver: (body: {
    cost_centre_code: string;
    cost_element: string;
    basis: string;
    source: string;
    valid_from: string;
  }) => request("/api/costing/drivers", { method: "POST", body }),

  runCosting: (periodMonth: string) =>
    request<CostRunResult>(`/api/costing/run?period_month=${periodMonth}`, {
      method: "POST",
    }),

  costRuns: () => request<CostRunResult[]>("/api/costing/runs"),

  // -- Phase 5: stock counts ----------------------------------------------
  dueCounts: () => request<DueCount[]>("/api/stock-count/due"),

  openCount: (body: {
    location_code: string;
    count_date: string;
    count_type: string;
    item_codes?: string[] | null;
  }) => request<CountResult>("/api/stock-count", { method: "POST", body }),

  recordCount: (
    id: number,
    quantities: Record<string, number>,
    recounts?: Record<string, number>
  ) =>
    request<CountResult>(`/api/stock-count/${id}/record`, {
      method: "POST",
      body: { quantities, recounts: recounts ?? null },
    }),

  signOffCount: (
    id: number,
    reasons: Record<string, number>,
    notes?: Record<string, string>
  ) =>
    request<CountResult>(`/api/stock-count/${id}/sign-off`, {
      method: "POST",
      body: { reasons, notes: notes ?? null },
    }),

  adjustCount: (id: number) =>
    request<CountResult>(`/api/stock-count/${id}/adjust`, { method: "POST" }),

  // -- Phase 5: intake ----------------------------------------------------
  templates: () => request<TemplateSpec[]>("/api/intake/templates"),

  // -- Phase 6: period close ----------------------------------------------
  closeStatus: (periodMonth: string) =>
    request<CloseStatus>(`/api/close/status?period_month=${periodMonth}`),

  closePeriod: (periodMonth: string, overrideReason?: string) =>
    request<CloseStatus>("/api/close", {
      method: "POST",
      body: {
        period_month: periodMonth,
        override_reason: overrideReason ?? null,
      },
    }),

  // -- Settings: demo data ------------------------------------------------
  // Super admin only; the server checks the role on every one of these.
  demoStatus: () => request<DemoStatus>("/api/admin/demo-data"),

  loadDemoData: (days: number, force = false) =>
    request<DemoLoadResult>("/api/admin/demo-data", {
      method: "POST",
      body: { days, force },
    }),

  removeDemoData: () =>
    request<DemoPurgeResult>("/api/admin/demo-data", { method: "DELETE" }),
};

/** Intake uploads use FormData, so they bypass the JSON request helper. */
export async function uploadIntake(
  template: string,
  file: File,
  mode: "preview" | "commit"
): Promise<IntakePreview> {
  const form = new FormData();
  form.append("file", file);
  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const response = await fetch(`${BASE}/api/intake/${template}/${mode}`, {
    method: "POST",
    headers,
    body: form,
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    throw new ApiError(
      response.status,
      typeof payload?.detail === "string"
        ? payload.detail
        : `Upload failed (${response.status})`
    );
  }
  return payload as IntakePreview;
}

/** A download that carries the auth header — a plain link would be anonymous. */
export async function downloadFile(
  path: string,
  filename: string,
  { method = "GET", body }: { method?: string; body?: unknown } = {}
): Promise<void> {
  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (body !== undefined) headers["Content-Type"] = "application/json";

  const response = await fetch(`${BASE}${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!response.ok) {
    throw new ApiError(response.status, "Could not download that file.");
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  // Firefox ignores a click on an anchor that is not in the document, so the
  // download simply did not happen there. And revoking the object URL in the
  // same tick can pull the blob out from under a download that has not
  // started yet — the click is queued, not synchronous.
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
}
